// Le planning attribué (D-144, maquettes p1 à p4, m1, m2, s1, s2, r1, r2, n1, f1, d1), logique pure et testée (plages.test.ts) :
//   * les PLAGES du club (ce que chaque gestionnaire donne : salle, jour, de… à…, validité, usage), puis les créneaux des
//     équipes rangés dedans ; le temps libre d'une plage ; les matchs à domicile qui s'y rangent (la base dit dans quelle
//     plage : hall_plages_view.placed), ceux qui en sortent ; les créneaux demandés par une dérogation en cours (fantômes) ;
//   * la barre horaire d'une plage (réglages, feuilles) et les cases de la semaine du club (D-138) avec la plage en cadre ;
//   * la carte unique de la reprise ; le lieu FFBB à relier (la salle la plus proche : nom, commune, distance).
// Les mêmes règles qu'en base (migration 20261105000000_planning_attribue.sql : _hall_plage_at, _hall_plage_fill).
import { isoDay } from './salles';
import { dur, fromMin, hShort, teamShort, toMin, WEEKDAYS, type Board, type BSlot } from './creneaux';
import { derogStates, type DerogTrack, type StepState } from './derogation-suivi';
import type { Cell } from './semaine';

// --- Ce que la base renvoie (hall_plages_view) --------------------------------------------------------------------------
export type PlageUsage = 'all' | 'matches' | 'trainings';
export interface Plage {
  id: string; hall_id: string; season: string; weekday: number; starts: string; ends: string;
  valid_from: string | null; valid_to: string | null; usage: PlageUsage; origin: 'club' | 'reprise'; slots: string[];
}
export interface Ghost { day: string; starts: string; ends: string; hall_id: string; plage_id: string | null }
export interface DerogLive { id: string | null; event_id: string; opponent: string | null; track: DerogTrack; ghosts: Ghost[] }
export interface OutMatch { event_id: string; team_id: string; hall_id: string; day: string; starts: string; ends: string; squad_id: string | null; opponent: string | null; derog: boolean; lost: boolean }
export interface VenueMatch { event_id: string; starts_at: string; team_id: string; squad_id: string | null; opponent: string | null }
export interface Venue { id: string; label: string; name: string; town: string | null; lat: number | null; lng: number | null; status: string; hall_id: string | null; matches: VenueMatch[] }
export interface Proposal { key: string; hall_id: string; weekday: number; n: number; days: string[]; starts: string; ends: string }
export interface Reprise { season: string; state: 'to_check' | 'done'; slots: number; plages: number; declined: string[]; checked_at: string | null; by: string | null }
export interface PlagesView {
  today: string; season: string; next_season: string; manage: boolean; bureau: boolean;
  plages: Plage[]; placed: { event_id: string; day: string; plage_id: string | null }[]; derogs: DerogLive[]; wait_days: number | null;
  reprise: Reprise | null; proposals: Proposal[]; out: OutMatch[]; venues: Venue[]; geo: { id: string; lat: number | null; lng: number | null; town: string | null }[];
}
export const EMPTY_VIEW: PlagesView = { today: '', season: '', next_season: '', manage: false, bureau: false, plages: [], placed: [], derogs: [], wait_days: 3,
  reprise: null, proposals: [], out: [], venues: [], geo: [] };

// --- Mots ----------------------------------------------------------------------------------------------------------------
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** « 14h–19h30 » */
export const rng = (a: string, z: string) => `${hShort(a)}–${hShort(z)}`;
/** « 14h00–19h30 » */
export const rngL = (a: string, z: string) => `${Number(a.slice(0, 2))}h${a.slice(3, 5)}–${Number(z.slice(0, 2))}h${z.slice(3, 5)}`;
/** « 18h00 » */
export const hh = (t: string) => `${Number(t.slice(0, 2))}h${t.slice(3, 5)}`;
export const plageMins = (p: Pick<Plage, 'starts' | 'ends'>) => toMin(p.ends) - toMin(p.starts);
export const totalMins = (list: Pick<Plage, 'starts' | 'ends'>[]) => list.reduce((s, p) => s + plageMins(p), 0);
/** « 14 plages · 36 h par semaine » */
export const plagesLine = (list: Pick<Plage, 'starts' | 'ends'>[]) => `${list.length} plage${list.length > 1 ? 's' : ''} · ${dur(totalMins(list))} par semaine`;
/** « Mairie de Rivebelle » → « la mairie de Rivebelle » ; « Communauté de communes des Deux Rives » → « la communauté de communes » ;
 *  « Collège Bellevue » → « le collège » ; sinon « le gestionnaire ». */
export function mgrThe(name: string | null | undefined, kind?: string | null): string {
  const n = (name ?? '').trim();
  if (/^mairie/i.test(n)) return `la ${n.charAt(0).toLowerCase()}${n.slice(1)}`;
  if (/^communaut/i.test(n)) return 'la communauté de communes';
  if (/^coll[eè]ge/i.test(n)) return 'le collège';
  if (/^lyc[eé]e/i.test(n)) return 'le lycée';
  if (kind === 'club') return 'le club';
  if (kind === 'commune') return 'la mairie';
  return 'le gestionnaire';
}
/** « au Gymnase de Rivebelle », « à la Salle Bellevue », « au Dojo municipal ». */
export function aHall(name: string | null | undefined): string {
  const n = (name ?? 'cette salle').trim();
  if (/^(gymnase|dojo|complexe|palais|stade|club)/i.test(n)) return `au ${n}`;
  if (/^(salle|halle|maison)/i.test(n)) return `à la ${n}`;
  return `à ${n}`;
}
const USAGE: Record<PlageUsage, string> = { all: 'matchs et entraînements', matches: 'les matchs', trainings: 'les entraînements' };
export const usageLine = (u: PlageUsage) => USAGE[u];
/** « toute la saison », « du 2 nov. au 31 mars ». */
export function validLine(p: Pick<Plage, 'valid_from' | 'valid_to'>): string {
  const MO = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
  const d = (s: string) => `${Number(s.slice(8, 10)) === 1 ? '1er' : Number(s.slice(8, 10))} ${MO[Number(s.slice(5, 7)) - 1]}`;
  if (!p.valid_from && !p.valid_to) return 'toute la saison';
  if (p.valid_from && p.valid_to) return p.valid_from === p.valid_to ? `le ${d(p.valid_from)} seulement` : `du ${d(p.valid_from)} au ${d(p.valid_to)}`;
  return p.valid_from ? `dès le ${d(p.valid_from)}` : `jusqu’au ${d(p.valid_to!)}`;
}

// --- Les plages d'un jour ---------------------------------------------------------------------------------------------
/** La saison (« 2026-2027 ») d'un jour : du 1er juillet au 30 juin. */
export const seasonOfDay = (d: string) => { const y = Number(d.slice(0, 4)), m = Number(d.slice(5, 7)); return m >= 7 ? `${y}-${y + 1}` : `${y - 1}-${y}`; };
export const validOn = (p: Pick<Plage, 'valid_from' | 'valid_to'>, day: string) => (!p.valid_from || day >= p.valid_from) && (!p.valid_to || day <= p.valid_to);
/** Les plages d'une salle ce jour-là (sa saison, son jour de la semaine, sa validité), dans l'ordre des heures. */
export function plagesOn(plages: Plage[], hallId: string, day: string): Plage[] {
  const s = seasonOfDay(day), w = isoDay(day);
  return plages.filter(p => p.hall_id === hallId && p.season === s && p.weekday === w && validOn(p, day)).sort((a, z) => a.starts.localeCompare(z.starts));
}
const within = (p: { starts: string; ends: string }, a: string, z: string) => toMin(a) >= toMin(p.starts) && toMin(z) <= toMin(p.ends);
/** La plage qui contient ce moment (comme _hall_plage_at : la plus courte). */
export function plageAt(plages: Plage[], hallId: string, day: string, a: string, z: string): Plage | null {
  return plagesOn(plages, hallId, day).filter(p => within(p, a, z)).sort((x, y) => plageMins(x) - plageMins(y))[0] ?? null;
}
/** Un créneau d'équipe a-t-il lieu ce jour-là ? (sa plage valide ce jour ; un créneau sans plage connue : oui). */
export function slotOn(plages: Plage[], slot: Pick<BSlot, 'id'>, day: string): boolean {
  const p = plages.find(x => x.slots.includes(slot.id));
  return !p || validOn(p, day);
}

// --- La barre horaire d'une plage -----------------------------------------------------------------------------------------
export type SegKind = 'team' | 'free' | 'asked' | 'match' | 'ghost';
export interface Seg {
  a: string; z: string; kind: SegKind; label: string; name: string; color?: string; teamId?: string | null; slotId?: string;
  eventId?: string; derog?: DerogLive | null; ghost?: Ghost; askWho?: string | null; gap?: boolean;
}
/** Retirer des intervalles occupés d'un intervalle : les morceaux qui restent (45 min au moins, comme _hall_plage_fill). */
export function minus(a: string, z: string, busy: [string, string][], min = 45): [string, string][] {
  const out: [string, string][] = [];
  let cur = toMin(a);
  const end = toMin(z);
  for (const [b0, b1] of [...busy].map(([x, y]) => [toMin(x), toMin(y)] as [number, number]).sort((x, y) => x[0] - y[0])) {
    if (b1 <= cur || b0 >= end) continue;
    if (b0 - cur >= min) out.push([fromMin(cur), fromMin(Math.min(b0, end))]);
    cur = Math.max(cur, b1);
  }
  if (end - cur >= min) out.push([fromMin(cur), fromMin(end)]);
  return out;
}
interface SegCtx { b: Pick<Board, 'slots' | 'teams' | 'occ' | 'asks'>; v: Pick<PlagesView, 'placed' | 'derogs'>; colors: Map<string, string> }
/** Ce qui occupe une plage : ses créneaux d'équipe, et pour un jour donné ses matchs (rangés d'office) et les créneaux
 *  demandés par une dérogation (fantômes) ; le reste est libre (un créneau libre et ses demandes, ou du temps non donné). */
export function plageSegs(ctx: SegCtx, pl: Plage, day: string | null): Seg[] {
  const { b, v, colors } = ctx;
  const team = (id: string | null) => (id ? b.teams.find(t => t.id === id) ?? null : null);
  const busy: Seg[] = [];
  const slots = b.slots.filter(s => pl.slots.includes(s.id));
  for (const s of slots.filter(x => x.team_id)) {
    const t = team(s.team_id);
    busy.push({ a: s.starts.slice(0, 5), z: s.ends.slice(0, 5), kind: 'team', label: t ? teamShort(t.name) : 'Équipe', name: t?.name ?? 'Équipe',
      color: colors.get(s.team_id!), teamId: s.team_id, slotId: s.id });
  }
  if (day) {
    for (const x of v.placed.filter(y => y.plage_id === pl.id && y.day === day)) {
      const o = b.occ.find(y => y.event_id === x.event_id && y.day === day && !y.cancelled);
      if (!o) continue;
      const t = team(o.team_id), sq = t?.squads?.find(q => q.id === o.squad_id)?.name;
      const who = sq ?? (t ? teamShort(t.name) : 'Match');
      busy.push({ a: clock(o.starts), z: clockEnd(o.starts, o.ends), kind: 'match', label: who, name: `${who} – ${o.opponent ?? o.title}`, color: colors.get(o.team_id),
        teamId: o.team_id, eventId: o.event_id, derog: v.derogs.find(d => d.event_id === o.event_id) ?? null });
    }
    for (const d of v.derogs) for (const g of d.ghosts.filter(y => y.day === day && y.hall_id === pl.hall_id && within(pl, y.starts, y.ends))) {
      const who = derogWho(b, d);
      busy.push({ a: g.starts, z: g.ends, kind: 'ghost', label: `${who}`, name: `${who}${d.opponent ? ` – ${d.opponent}` : ''}`, derog: d, ghost: g, color: 'var(--match)' });
    }
  }
  const taken = busy.map(x => [x.a, x.z] as [string, string]);
  const out: Seg[] = [...busy];
  // Les créneaux libres (et leurs demandes), moins ce qui est pris ce jour-là.
  const frees = slots.filter(x => !x.team_id);
  for (const s of frees) {
    const ask = b.asks.find(a => a.slot_id === s.id && a.status === 'pending');
    for (const [a, z] of minus(s.starts.slice(0, 5), s.ends.slice(0, 5), taken)) {
      out.push({ a, z, kind: ask ? 'asked' : 'free', label: 'Libre', name: 'Libre', slotId: s.id, askWho: ask?.who ?? null });
    }
  }
  // Le temps que rien ne couvre (une plage sans créneau libre : « Libre », pas encore donné).
  const covered = [...taken, ...frees.map(s => [s.starts.slice(0, 5), s.ends.slice(0, 5)] as [string, string])];
  for (const [a, z] of minus(pl.starts, pl.ends, covered)) out.push({ a, z, kind: 'free', label: 'Libre', name: 'Libre', gap: true });
  return out.sort((x, y) => x.a.localeCompare(y.a) || toMin(y.z) - toMin(x.z));
}
/** Les minutes libres d'une plage (ce que montre la barre). */
export const freeMins = (segs: Seg[]) => segs.filter(s => s.kind === 'free' || s.kind === 'asked').reduce((n, s) => n + toMin(s.z) - toMin(s.a), 0);
/** « 18:00 » d'un instant (heure de l'appareil, celle du club). */
const clock = (iso: string) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
/** La fin d'un match le même jour (23:59 au plus). */
const clockEnd = (a: string, z: string) => { const s = new Date(a), e = new Date(z); return e.getDate() !== s.getDate() ? '23:59' : clock(z); };
/** Le nom court de notre équipe d'une dérogation (« SF1 »). */
export function derogWho(b: Pick<Board, 'teams' | 'occ'>, d: DerogLive): string {
  const o = b.occ.find(x => x.event_id === d.event_id);
  const t = o ? b.teams.find(x => x.id === o.team_id) : null;
  return (o && t?.squads?.find(q => q.id === o.squad_id)?.name) ?? (t ? teamShort(t.name) : 'Notre match');
}

// --- L'étape d'une dérogation (la pastille) --------------------------------------------------------------------------------
/** « adversaire prévenu », « accord reçu », « saisie sur FBI », « demandée par Mirvallon »… */
export function derogStep(d: Pick<DerogLive, 'track' | 'opponent'>): string {
  const t = d.track;
  if (t.direction === 'in') return t.filed_at || t.status === 'filed' ? 'saisie sur FBI' : t.our_answer === 'accept' ? 'acceptée, à saisir' : `demandée par ${d.opponent ?? 'l’autre club'}`;
  if (t.filed_at || t.status === 'filed') return 'saisie sur FBI';
  return t.answer === 'accept' ? 'accord reçu' : t.answer === 'counter' ? 'contre-proposition' : t.answer === 'refuse' ? 'refusée par le club' : 'adversaire prévenu';
}
/** Les 4 étapes (D-137) d'une dérogation lue du planning. */
export const derogBar = (d: DerogLive, waitDays: number | null, now = new Date()): StepState[] => derogStates(d.track, now, waitDays ?? 3);

// --- Les cases de la semaine (D-138) avec les plages en cadre -----------------------------------------------------------
export interface Box { plage: Plage; items: (Cell & { gap?: boolean; ghost?: Ghost; derog?: DerogLive | null })[] }
export interface PCell { boxes: Box[]; rest: (Cell & { out?: boolean; ghost?: Ghost; derog?: DerogLive | null })[] }
/** Une case (une salle, un jour) : chaque plage valide (ses heures en tête) avec ce qui s'y passe et son temps libre ; ce qui
 *  sort de toute plage dessous (un match ou un créneau demandé : « hors plage », en orange). */
export function plageCell(cells: Cell[], plages: Plage[], v: Pick<PlagesView, 'derogs' | 'placed'>, b: Pick<Board, 'teams' | 'occ'>, day: string, col: string, wide = false): PCell {
  const its: (Cell & { ghost?: Ghost; derog?: DerogLive | null })[] = cells.filter(x => x.col === col).map(x => {
    const dg = x.eventId && /match/.test(x.cls) ? v.derogs.find(d => d.event_id === x.eventId) ?? null : null;
    return dg && !/hit/.test(x.cls) ? { ...x, cls: `${x.cls} pa-derog`, sub: wide ? `${hShort(x.start)} · ${derogStep(dg)}` : 'dérogation', derog: dg } : x;
  });
  for (const d of v.derogs) for (const g of d.ghosts.filter(y => y.day === day && y.hall_id === col)) {
    const who = derogWho(b, d);
    its.push({ day, col, start: g.starts, end: g.ends, cls: 'match pa-ghost', label: wide && d.opponent ? `${who} – ${d.opponent}` : who,
      sub: wide ? `${hShort(g.starts)} · demandé, réservé` : 'demandé', color: 'var(--match)', ghost: g, derog: d, eventId: d.event_id });
  }
  its.sort((a, z) => a.start.localeCompare(z.start));
  const used = new Set<object>();
  const boxes: Box[] = plagesOn(plages, col, day).map(pl => {
    const inn = its.filter(x => !used.has(x) && within(pl, x.start, x.end));
    inn.forEach(x => used.add(x));
    // Les créneaux libres : coupés par ce qui est pris ce jour-là ; le temps non couvert : « Libre ».
    const taken = inn.filter(x => !/^(free|asked)$/.test(x.cls)).map(x => [x.start, x.end] as [string, string]);
    const items: Box['items'] = inn.filter(x => !/^(free|asked)$/.test(x.cls));
    for (const f of inn.filter(x => /^(free|asked)$/.test(x.cls))) {
      for (const [a, z] of minus(f.start, f.end, taken)) items.push({ ...f, start: a, end: z, sub: f.cls === 'asked' ? f.sub : rng(a, z) });
    }
    const covered = inn.map(x => [x.start, x.end] as [string, string]);
    for (const [a, z] of minus(pl.starts, pl.ends, covered)) items.push({ day, col, start: a, end: z, cls: 'free pa-gap', label: 'Libre', sub: rng(a, z), gap: true });
    items.sort((a, z) => a.start.localeCompare(z.start));
    return { plage: pl, items };
  });
  const rest: PCell['rest'] = its.filter(x => !used.has(x)).map(x => {
    if (!/match/.test(x.cls) || /hit/.test(x.cls)) return x;
    if (x.ghost) return { ...x, cls: `${x.cls} pa-out`, sub: wide ? `${hShort(x.start)} · demandé, hors plage` : 'demandé', out: true };
    if (x.derog) return { ...x, cls: `${x.cls} pa-out`, out: true };   // la dérogation en cours traite déjà le « hors plage »
    return { ...x, cls: `${x.cls} pa-out`, sub: wide ? `${hShort(x.start)} · hors plage` : 'hors plage', out: true };
  });
  return { boxes, rest };
}

// --- La reprise : la carte unique -----------------------------------------------------------------------------------------
export interface RepLine { kind: 'merged' | 'twin' | 'prop'; title: string; sub: string; plages: Plage[]; prop?: Proposal }
/** Les lignes de la carte (r1) : les plus gros regroupements, un trou (« Réunir »), les plages proposées d'après les matchs. */
export function repriseLines(b: Pick<Board, 'halls' | 'slots' | 'teams'>, plages: Plage[], props: Proposal[], season: string): RepLine[] {
  const name = (id: string) => b.halls.find(h => h.id === id)?.name ?? 'Salle';
  const own = plages.filter(p => p.season === season);
  const out: RepLine[] = [];
  const merged = own.filter(p => p.origin === 'reprise' && p.slots.length > 1).sort((a, z) => z.slots.length - a.slots.length || plageMins(z) - plageMins(a));
  for (const p of merged.slice(0, 2)) {
    const who = p.slots.map(id => b.slots.find(s => s.id === id)).filter((s): s is BSlot => !!s).sort((a, z) => a.starts.localeCompare(z.starts))
      .map(s => { const t = s.team_id ? b.teams.find(x => x.id === s.team_id) : null; return t ? teamShort(t.name) : 'libre'; });
    out.push({ kind: 'merged', plages: [p], title: `${name(p.hall_id)}, ${WEEKDAYS[p.weekday - 1]} : ${p.slots.length} créneaux → 1 plage ${rng(p.starts, p.ends)}`,
      sub: `${who.join(', ')} : chacun garde son créneau dedans` });
  }
  const seen = new Set<string>();
  for (const p of own) {
    const k = `${p.hall_id}|${p.weekday}`;
    if (seen.has(k)) continue;
    const twins = own.filter(x => x.hall_id === p.hall_id && x.weekday === p.weekday && !x.valid_from && !x.valid_to).sort((a, z) => a.starts.localeCompare(z.starts));
    if (twins.length < 2) continue;
    seen.add(k);
    out.push({ kind: 'twin', plages: twins, title: `${name(p.hall_id)}, ${WEEKDAYS[p.weekday - 1]} : ${twins.length} plages (${twins.map(x => rng(x.starts, x.ends)).join(', ')})`,
      sub: `Une seule si le gestionnaire vous donne ${rng(twins[0]!.starts, twins[twins.length - 1]!.ends)} : « Réunir »` });
    if (out.filter(x => x.kind === 'twin').length >= 1) break;
  }
  for (const pr of props.slice(0, 2)) {
    const h = name(pr.hall_id);
    out.push({ kind: 'prop', plages: [], prop: pr, title: `Tes matchs du ${WEEKDAYS[pr.weekday - 1]} ${/^gymnase/i.test(h) && !/coll/i.test(h) ? 'au gymnase' : aHall(h)} ne tombent dans aucune plage`,
      sub: `${pr.n} d’ici 6 semaines : on te propose la plage du ${WEEKDAYS[pr.weekday - 1]} ${rng(pr.starts, pr.ends)}, d’après leurs horaires` });
  }
  return out;
}
/** « On a repris tes 17 créneaux en 13 plages » : ce que la reprise a fait (la saison en cours). */
export function repriseCount(b: Pick<Board, 'slots'>, plages: Plage[], season: string): { slots: number; plages: number } {
  const own = plages.filter(p => p.season === season);
  const ids = new Set(own.flatMap(p => p.slots));
  return { slots: b.slots.filter(s => ids.has(s.id)).length, plages: own.length };
}

// --- Les lieux FFBB à relier : la salle la plus proche --------------------------------------------------------------------
const STOP = new Set(['salle', 'gymnase', 'gym', 'complexe', 'sportif', 'sports', 'omnisports', 'omnisport', 'palais', 'des', 'de', 'du', 'la', 'le', 'les', 'l', 'd', 'et', 'municipal', 'municipale']);
const words = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(w => w && !STOP.has(w));
/** Deux noms proches : un mot qui compte en commun (« Salle omnisports Bellevue » et « Salle Bellevue »). */
export function nameScore(a: string, b: string): number {
  const x = new Set(words(a)), y = new Set(words(b));
  if (!x.size || !y.size) return 0;
  let n = 0; for (const w of x) if (y.has(w)) n++;
  return n / Math.min(x.size, y.size);
}
/** Distance en mètres (haversine). */
export function meters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000, rad = (d: number) => d * Math.PI / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
}
/** « à 80 m », « à 1,2 km » */
export const distLine = (m: number) => (m < 1000 ? `à ${Math.max(10, Math.round(m / 10) * 10)} m` : `à ${(m / 1000).toFixed(1).replace('.', ',')} km`);
export interface Near { hall_id: string; score: number; name: boolean; town: boolean; meters: number | null }
/** Les salles du club, de la plus proche à la moins proche du lieu FFBB (nom, commune, distance si les positions existent). */
export function closestHalls(venue: Pick<Venue, 'name' | 'town' | 'lat' | 'lng'>, halls: { id: string; name: string; town: string | null; kind?: string }[],
  geo: { id: string; lat: number | null; lng: number | null }[] = []): Near[] {
  const norm = (s: string | null | undefined) => (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  return halls.filter(h => h.kind !== 'reception').map(h => {
    const g = geo.find(x => x.id === h.id);
    const m = venue.lat != null && venue.lng != null && g?.lat != null && g?.lng != null ? meters({ lat: venue.lat, lng: venue.lng }, { lat: g.lat, lng: g.lng }) : null;
    const ns = nameScore(venue.name, h.name);
    const town = !!venue.town && !!h.town && norm(venue.town) === norm(h.town);
    const score = ns * 3 + (town ? 1 : 0) + (m == null ? 0 : m < 300 ? 2 : m < 2000 ? 1 : 0);
    return { hall_id: h.id, score, name: ns >= 0.5, town, meters: m };
  }).sort((a, z) => z.score - a.score);
}
/** « Nom proche · même commune (Rivebelle) · à 80 m » */
export function nearLine(n: Near, town: string | null): string {
  return [n.name ? 'Nom proche' : '', n.town ? `même commune${town ? ` (${town})` : ''}` : '', n.meters != null ? distLine(n.meters) : ''].filter(Boolean).join(' · ');
}

// --- Un match hors de toute plage ----------------------------------------------------------------------------------------
/** Pourquoi un match sort du planning attribué (m2) : « Le samedi, la plage du club au Gymnase de Rivebelle va de 10h à 11h30 :
 *  ce match en sort. » */
export function outWhy(plages: Plage[], m: Pick<OutMatch, 'hall_id' | 'day' | 'starts' | 'ends'>, hallName: string): string {
  const wd = WEEKDAYS[isoDay(m.day) - 1]!;
  const same = plagesOn(plages, m.hall_id, m.day);
  const near = same.find(p => toMin(p.starts) < toMin(m.ends) && toMin(m.starts) < toMin(p.ends));
  const hs = (t: string) => hShort(t);
  if (near) return `Le ${wd}, la plage du club ${aHall(hallName)} va de ${hs(near.starts)} à ${hs(near.ends)} : ce match en sort.`;
  if (same.length) return `Le ${wd}, le club n’a que ${same.map(p => rng(p.starts, p.ends)).join(' et ')} ${aHall(hallName)}.`;
  return `Le ${wd}, le club n’a aucune plage ${aHall(hallName)}.`;
}
/** La plage qu'on noterait pour ce match (« La mairie nous l'a déjà donnée ») : celle du jour agrandie, sinon le match. */
export function plageFor(plages: Plage[], m: Pick<OutMatch, 'hall_id' | 'day' | 'starts' | 'ends'>): { plage: Plage | null; starts: string; ends: string } {
  const near = plagesOn(plages, m.hall_id, m.day).find(p => toMin(p.starts) < toMin(m.ends) && toMin(m.starts) < toMin(p.ends)) ?? null;
  if (near) return { plage: near, starts: toMin(m.starts) < toMin(near.starts) ? m.starts : near.starts, ends: toMin(m.ends) > toMin(near.ends) ? m.ends : near.ends };
  return { plage: null, starts: m.starts, ends: m.ends };
}

export const cap1 = cap;
